import React, { useEffect, useRef, useId } from "react";
import { createPortal } from "react-dom";
import { MdClose } from "react-icons/md";
import MessageBanner from "./MessageBanner";

export type ModalSize = "sm" | "md" | "lg" | "2xl" | "3xl" | "4xl" | "6xl";

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "6xl": "max-w-6xl",
};

export interface ModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  // Rendered to the left of the title, e.g. a brand-coloured icon
  icon?: React.ReactNode;
  // "title": bold h2 over a divider - forms and detail views.
  // "compact": h3 beside the icon, no divider - confirmation dialogs.
  headerVariant?: "title" | "compact";
  size?: ModalSize;
  // "column" (default): header, message and footer stay pinned; the body
  //   scrolls between them. The shell wraps children in the scrolling
  //   region, so a plain body works; a body with its own pinned parts
  //   (a search bar over a list) can still manage an inner scroller.
  // "scroll": the whole panel scrolls, footer included. For wide two-panel
  //   assignment modals and read-only detail views with a sticky header.
  // "fixed": no height cap - short dialogs that never need to scroll.
  layout?: "scroll" | "column" | "fixed";
  // Keep the header visible while a long detail view scrolls beneath it
  stickyHeader?: boolean;
  // Disables the close button and overlay while a request is in flight
  closeDisabled?: boolean;
  // Rendered under a full-width divider at the bottom of the panel
  footer?: React.ReactNode;
  // A request error or success line. Rendered directly above the footer, so
  // it is always next to the button that was just clicked - a banner at the
  // top of a long form is off-screen when the user is at the Save button.
  message?: { text: string; type: "error" | "success" } | null;
  // Stacking order. The default sits above the app; "nested" is for a modal
  // opened from inside another modal (the ingredient picker over the recipe
  // form), which is the reason that one used to fork this component.
  depth?: "base" | "nested";
  // When set, body and footer are wrapped in a <form>, so a type="submit"
  // button in the footer submits it even though the footer sits outside
  // the children.
  onSubmit?: (e: React.FormEvent) => void;
  children: React.ReactNode;
}

const LAYOUT_CLASSES = {
  scroll: "max-h-[90vh] overflow-y-auto",
  column: "flex max-h-[90vh] flex-col overflow-hidden",
  fixed: "",
};

// A wrapping <form> must not break the panel's layout: in a column panel
// it has to be the flex child that carries the scrolling body and pinned
// footer.
const BODY_CLASSES = {
  scroll: "",
  column: "flex min-h-0 flex-1 flex-col overflow-hidden",
  fixed: "",
};

// The one modal chrome for the dashboard: overlay, centred panel, header with
// close button, optional footer. Modals compose their body inside it; the
// former per-modal copies of this markup drifted in padding, borders and
// close-button colour, which this removes.
const ModalShell: React.FC<ModalShellProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  headerVariant = "title",
  size = "2xl",
  layout = "column",
  stickyHeader = false,
  closeDisabled = false,
  footer,
  message,
  depth = "base",
  onSubmit,
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Hooks must run unconditionally, so every effect below no-ops while closed
  // rather than sitting behind the early return.

  // Close on Escape. Nothing in the app did this before, so a modal could
  // only be dismissed by finding its close button.
  useEffect(() => {
    if (!isOpen || closeDisabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, closeDisabled, onClose]);

  // Stop the page behind the overlay from scrolling.
  useEffect(() => {
    if (!isOpen) return;

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  // Move focus into the panel, and restore it to whatever opened the modal.
  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const firstField = panelRef.current?.querySelector<HTMLElement>(
      "input, select, textarea, button:not([aria-label='Close'])"
    );
    (firstField ?? panelRef.current)?.focus();

    return () => previouslyFocused?.focus?.();
  }, [isOpen]);

  // Keep Tab inside the panel while it is open.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Tab" || !panelRef.current) return;

    const focusable = Array.from(
      panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!isOpen) return null;

  const handleClose = () => {
    if (!closeDisabled) onClose();
  };

  const header =
    headerVariant === "compact" ? (
      <div className="flex items-center justify-between p-6 pb-0">
        <div className="flex items-center space-x-3">
          {icon && <div className="flex-shrink-0">{icon}</div>}
          <h3
            id={titleId}
            className="text-lg font-semibold text-gray-900 dark:text-white"
          >
            {title}
          </h3>
        </div>
        <CloseButton onClick={handleClose} disabled={closeDisabled} />
      </div>
    ) : (
      <div
        className={`flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-800 ${
          stickyHeader ? "sticky top-0 z-10 bg-white py-4 dark:bg-navy-800" : ""
        }`}
      >
        <div className="flex items-center gap-3">
          {icon}
          <div>
            <h2
              id={titleId}
              className="text-xl font-bold text-navy-700 dark:text-white"
            >
              {title}
            </h2>
            {subtitle && (
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        <CloseButton onClick={handleClose} disabled={closeDisabled} />
      </div>
    );

  // Rendered in a portal so a modal is never clipped or re-stacked by an
  // ancestor's overflow or transform.
  const banner = message ? (
    <MessageBanner message={message.text} type={message.type} />
  ) : null;

  // In a column panel the body is the one flex child allowed to scroll;
  // the banner and footer after it stay in view.
  const body =
    layout === "column" ? (
      <div
        data-testid="modal-body"
        className="flex min-h-0 flex-1 flex-col overflow-y-auto"
      >
        {children}
      </div>
    ) : (
      children
    );

  return createPortal(
    <div
      className={`fixed inset-0 overflow-y-auto overscroll-contain ${
        depth === "nested" ? "z-[60]" : "z-50"
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onKeyDown={handleKeyDown}
    >
      {/* Background overlay */}
      <div
        className="fixed inset-0 bg-gray-400 bg-opacity-75 transition-opacity dark:bg-gray-900 dark:bg-opacity-75"
        onClick={handleClose}
        aria-hidden="true"
        data-testid="modal-overlay"
      />

      <div className="flex min-h-screen items-center justify-center px-4 py-6">
        <div
          ref={panelRef}
          tabIndex={-1}
          className={`relative z-10 w-full rounded-lg border border-gray-200 bg-white shadow-xl outline-none dark:border-gray-600 dark:bg-navy-800 ${SIZE_CLASSES[size]} ${LAYOUT_CLASSES[layout]}`}
        >
          {header}
          {onSubmit ? (
            <form onSubmit={onSubmit} className={BODY_CLASSES[layout]}>
              {body}
              {banner}
              {footer && <ModalFooter>{footer}</ModalFooter>}
            </form>
          ) : (
            <>
              {body}
              {banner}
              {footer && <ModalFooter>{footer}</ModalFooter>}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

// Right-aligned action row under a full-width divider - the same footer on
// every modal, from confirm dialogs to full forms.
const ModalFooter: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-center justify-end space-x-3 border-t border-gray-200 p-6 dark:border-gray-800">
    {children}
  </div>
);

const CloseButton: React.FC<{ onClick: () => void; disabled: boolean }> = ({
  onClick,
  disabled,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label="Close"
    className="text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50 dark:hover:text-gray-300"
  >
    <MdClose className="h-6 w-6" />
  </button>
);

export default ModalShell;
